import { Controller } from "@hotwired/stimulus"
import { cable } from "../lair/cable.js"
import { chime } from "../lair/chime.js"

// The moments the game says out loud (Announcements): a level of keeping
// reached, a dragon grown a level. AnnouncementsChannel streams the keeper's
// own cards and replays the unseen ones on subscribe, so a level that rose
// in a request that redirected still gets its moment on the next page.
//
// One card at a time, over the page, with the fanfare; it leaves on its own
// or when pressed, and tells the channel it was seen so it is not replayed.
const SHOW_MS = 10000
const LEAVE_MS = 320

export default class extends Controller {
  connect() {
    this.queue = []
    this.shownIds = new Set()
    this.subscription = cable().subscriptions.create("AnnouncementsChannel", {
      received: (card) => this.enqueue(card)
    })
  }

  disconnect() {
    clearTimeout(this.timer)
    this.subscription?.unsubscribe()
    this.subscription = null
    this.element.replaceChildren()
    this.current = null
  }

  enqueue(card) {
    if (!card?.id || this.shownIds.has(card.id)) return
    this.shownIds.add(card.id)
    this.queue.push(card)
    if (!this.current) this.next()
  }

  next() {
    const card = this.queue.shift()
    if (!card) { this.current = null; return }

    this.current = card
    this.element.replaceChildren(this.build(card))
    this.element.hidden = false
    chime("level")
    this.timer = setTimeout(() => this.dismiss(), SHOW_MS)
  }

  dismiss() {
    clearTimeout(this.timer)
    const card = this.current
    if (!card) return

    this.subscription?.perform("seen", { id: card.id })
    const node = this.element.firstElementChild
    node?.classList.add("ll-fanfare--leaving")
    setTimeout(() => {
      this.element.replaceChildren()
      this.element.hidden = true
      this.next()
    }, LEAVE_MS)
  }

  build(card) {
    const challenged = card.kind === "challenged"
    const dragon = card.kind === "dragon_level" || card.kind === "dragon_stage" || challenged
    const parcel = card.kind === "parcel"
    const trail = card.kind === "trail"
    const diary = card.kind === "diary"
    const hunt = card.kind === "hunt"
    const valley = card.kind === "valley"
    // A stage card has no number to show big: the stage's name goes there;
    // a parcel shows the parcel, a treasure trail its scroll.
    const big = card.kind === "dragon_stage" ? (card.stage || "").replace(/^./, (c) => c.toUpperCase())
      : parcel ? "\u{1F381}" : trail ? "\u{1F4DC}" : diary ? "\u{1F4D6}" : hunt ? "\u{1F3F9}" : valley ? "\u{1F52D}" : challenged ? `${card.won}–${card.lost}` : String(card.level)
    const scrim = el("div", "ll-fanfare")
    scrim.setAttribute("role", "status")
    scrim.setAttribute("aria-live", "polite")
    scrim.addEventListener("click", (event) => { if (event.target === scrim) this.dismiss() })

    const box = el("div", "ll-fanfare__card")
    box.append(
      el("p", "ll-fanfare__kicker ll-label ll-label--gold",
        parcel ? "The post" : trail ? "Treasure trail" : diary ? "Region diary" : hunt ? "Hunt task" : valley ? "Valley project" : dragon ? "Your dragon" : "Level of keeping"),
      el("p", card.kind === "dragon_stage" || parcel || trail || diary || hunt || valley || challenged ? "ll-fanfare__level ll-fanfare__level--word" : "ll-fanfare__level", big),
      el("h2", "ll-fanfare__heading", card.heading)
    )
    if (card.line) box.append(el("p", "ll-fanfare__title", card.line))
    if (card.opened?.length) {
      box.append(el("p", "ll-fanfare__opened ll-label", "Now open"))
      const list = el("ul", "ll-fanfare__list")
      for (const name of card.opened) list.append(el("li", null, name))
      box.append(list)
    }
    if (card.gold > 0) {
      box.append(el("p", "ll-fanfare__gold", `${card.gold} gold from the crown`))
    }

    const actions = el("div", "ll-fanfare__actions")
    const go = el("a", "ll-btn ll-btn--primary",
      trail ? (card.button || "See your clue") : diary ? (card.button || "See your diaries") : hunt ? (card.button || "Get another") : valley ? (card.button || "Go and see") : parcel ? "Open it" : card.kind === "dragon_stage" ? "Go and see" : challenged ? "To the Arena" : dragon ? "To the fights" : "See your level")
    go.href = card.href
    go.addEventListener("click", () => this.dismiss())
    const close = el("button", "ll-btn", "Carry on")
    close.type = "button"
    close.addEventListener("click", () => this.dismiss())
    actions.append(go, close)
    box.append(actions)

    scrim.append(box)
    return scrim
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}
